import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useState } from "react";
import { BrandLockup } from "@/features/marketing/components/BrandLockup";
import { ApiError, createOrganization } from "@/lib/api";
import type { OrganizationResponse } from "@/generated/api-client";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function describeError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 409) {
      return "That slug is already in use. Choose another one.";
    }
    if (cause.status === 401) {
      return "Your session expired. Sign in again to create an organization.";
    }
    if (cause.status === 400) {
      return "Check the organization name and slug, then try again.";
    }
  }
  return cause instanceof Error
    ? cause.message
    : "The organization could not be created. Please try again.";
}

export function CreateOrganizationPage({
  onCreated,
}: {
  onCreated: (organization: OrganizationResponse) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateName(nextName: string) {
    setName(nextName);
    setSlug((current) =>
      current === "" || current === slugify(name) ? slugify(nextName) : current,
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const trimmedName = name.trim();
    const normalizedSlug = slug.trim().toLowerCase();
    if (!trimmedName) {
      setError("Enter an organization name.");
      return;
    }
    if (
      normalizedSlug.length < 3 ||
      normalizedSlug.length > 63 ||
      !SLUG_PATTERN.test(normalizedSlug)
    ) {
      setError(
        "Use a slug of 3–63 lowercase letters, numbers, or single hyphens.",
      );
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const organization = await createOrganization({
        name: trimmedName,
        slug: normalizedSlug,
      });
      onCreated(organization);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-4 px-6 py-16">
      <BrandLockup className="h-12 w-12" />
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Create an organization</CardTitle>
          <CardDescription>
            Organizations hold business apps, legal entities, and extension
            installs. Add the first legal entity whose books the apps will
            keep after creating the organization. Business features require a
            separate Business plan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            aria-busy={busy}
            className="grid gap-5"
            onSubmit={(event) => void submit(event)}
          >
            <label className="grid gap-1 text-sm font-medium">
              Organization name
              <Input
                autoComplete="organization"
                autoFocus
                id="organization-name"
                disabled={busy}
                maxLength={120}
                onChange={(event) => updateName(event.target.value)}
                required
                value={name}
              />
            </label>
            <label className="grid gap-1 text-sm font-medium">
              URL slug
              <Input
                aria-describedby="organization-slug-help"
                autoCapitalize="none"
                id="organization-slug"
                autoComplete="off"
                disabled={busy}
                maxLength={63}
                minLength={3}
                onChange={(event) => setSlug(event.target.value)}
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                required
                spellCheck={false}
                value={slug}
              />
              <span
                className="text-xs font-normal text-[var(--muted-foreground)]"
                id="organization-slug-help"
              >
                Used in links. 3–63 lowercase letters, numbers, or single
                hyphens.
              </span>
            </label>
            {error ? (
              <p className="text-sm text-[var(--danger)]" role="alert">
                {error}
              </p>
            ) : null}
            <Button className="w-full" disabled={busy} type="submit">
              {busy ? "Creating organization..." : "Create organization"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <a
        className="text-center text-sm font-medium text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
        href="/home"
      >
        Back to apps
      </a>
    </main>
  );
}
